import { api } from '@shared/services/api/axios';
import { authApi } from '@shared/services/api/authApi';
import {
  buildLoginAuthErrorUrl,
  logAuthApiFailure,
  saveAuthApiFailure,
} from '@shared/utils/authApiFailure';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

const AUTH_REDIRECT_PATHS = new Set(['/login', '/callback']);

const extractErrorMessage = (error: {
  response?: { data?: { error?: string; message?: string } };
  message?: string;
}): string => {
  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    'Error de autenticación'
  );
};

const attachBearerRequest = (
  instance: AxiosInstance,
  getToken: () => Promise<string>
) => {
  return instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      try {
        const token = await getToken();
        if (!token) {
          return Promise.reject(new Error('No access token available'));
        }
        config.headers.Authorization = `Bearer ${token}`;
        return config;
      } catch (error) {
        // Sesión Auth0 inválida: getToken ya disparó re-login; no enviar sin Bearer.
        return Promise.reject(error);
      }
    },
    (error) => Promise.reject(error)
  );
};

export const configureAuthInterceptor = (getToken: () => Promise<string>) => {
  // Remover interceptores existentes para evitar duplicados (índices fijos frágiles;
  // eject por id devolvemos al cleanup).
  api.interceptors.request.eject(0);
  api.interceptors.response.eject(0);

  const apiRequestId = attachBearerRequest(api, getToken);
  const authApiRequestId = attachBearerRequest(authApi, getToken);

  const responseInterceptor = api.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status as number | undefined;
      const errorMsg = extractErrorMessage(error);
      const isCallbackUrlError = errorMsg.includes('Callback URL mismatch');
      const isAuthError = status === 401 || status === 403;
      const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
      const isOnAuthPage = AUTH_REDIRECT_PATHS.has(pathname);

      // En /callback y /login no forzar redirect: Callback/LoginForm manejan el estado
      // y un hard redirect a /login sin flag reabre el bucle (Auth0 autenticado → /home).
      if (isOnAuthPage) {
        return Promise.reject(error);
      }

      if (isCallbackUrlError || isAuthError) {
        const failure = {
          status: status ?? 0,
          url: String(error?.config?.url || error?.config?.baseURL || 'unknown'),
          method: String(error?.config?.method || 'get').toUpperCase(),
          message: errorMsg,
        };
        logAuthApiFailure(failure);
        saveAuthApiFailure(failure);

        // Query param sobrevive window.location (a diferencia de react-router state)
        window.location.href = buildLoginAuthErrorUrl();
      }

      return Promise.reject(error);
    }
  );

  // erlia-auth: solo 401 fuerza re-login. 403 (p.ej. sin manage:users) lo maneja la UI.
  const authApiResponseId = authApi.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status as number | undefined;
      const errorMsg = extractErrorMessage(error);
      const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
      const isOnAuthPage = AUTH_REDIRECT_PATHS.has(pathname);

      if (isOnAuthPage || status !== 401) {
        return Promise.reject(error);
      }

      const failure = {
        status: status ?? 0,
        url: String(error?.config?.url || error?.config?.baseURL || 'unknown'),
        method: String(error?.config?.method || 'get').toUpperCase(),
        message: errorMsg,
      };
      logAuthApiFailure(failure);
      saveAuthApiFailure(failure);
      window.location.href = buildLoginAuthErrorUrl();
      return Promise.reject(error);
    }
  );

  return () => {
    api.interceptors.request.eject(apiRequestId);
    api.interceptors.response.eject(responseInterceptor);
    authApi.interceptors.request.eject(authApiRequestId);
    authApi.interceptors.response.eject(authApiResponseId);
  };
};
